/**
 * MOBILE HERO — two intro pieces:
 *
 *  - `HeroMobileIntro`: centered H1 (two lines — "Kündige
 *    {article}" + target) + subhead + "Demo buchen ↗" primary button.
 *  - `HeroMobileClosingCta`: the closing dark CTA panel.
 *
 * Both are mobile-width specific (paddings + vertical button stack), so they do
 * NOT reuse the full-width desktop `HeroCta`.
 */
import Link from "next/link";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { HeroMobileRotatorHeadline } from "./hero-rotator";
import { H, MOBILE_HERO_BAND_GUTTER } from "./hero-tokens";
import { HeroCtaPanel, heroCtaEmphasis } from "./sections/hero-cta-panel";

/** SECTION 2 — hero copy + stacked button column. */
export function HeroMobileIntro(): ReactNode {
  const t = useTranslations("marketing.heroMobileCta");
  return (
    <div style={{ textAlign: "center", padding: "40px 28px 0" }}>
      <h1
        style={{
          fontFamily: H.display,
          fontWeight: 400,
          fontSize: 38,
          lineHeight: 1.12,
          letterSpacing: "-0.016em",
          color: H.ink,
          margin: "0 auto",
          maxWidth: "100%",
          padding: "0 4px",
        }}
      >
        <HeroMobileRotatorHeadline />
      </h1>

      <p
        style={{
          fontSize: 16,
          lineHeight: 1.5,
          color: H.inkMute,
          margin: "16px auto 0",
          maxWidth: 330,
        }}
      >
        {t("allesSoftwareSchlussTausend")}
      </p>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          marginTop: 24,
        }}
      >
        <Link
          href="/demo"
          className="mkhero-darkbtn"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 9,
            background: H.accent,
            color: "#fff",
            padding: "14px 26px",
            borderRadius: 12,
            fontSize: 16,
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          {t("demoBuchen")} <span style={{ fontSize: 13 }}>{"↗"}</span>
        </Link>
      </div>
    </div>
  );
}

/** SECTION 6 — closing dark CTA card. */
export function HeroMobileClosingCta(): ReactNode {
  const t = useTranslations("marketing.heroMobileCta");
  return (
    <div
      style={{
        margin: "40px auto 0",
        padding: `0 ${MOBILE_HERO_BAND_GUTTER}px`,
      }}
    >
      <HeroCtaPanel
        compact
        headline={t.rich("siehWieVielLeichter", { em: heroCtaEmphasis })}
        sub={t("persoenlichenDemoCallZeigen")}
        buttonLabel={t("demoCallBuchen")}
      />
    </div>
  );
}
